import { CSSProperties, ElementType, FC, ReactNode } from 'react';
import { Typography } from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';

import { COLORS } from '~/styles/colors';

type TextVariant =
  | 'buttonMedium'
  | 'buttonSmallRegular'
  | 'buttonSmallMedium'
  | 'buttonLinedMed'
  | 'button14Med'
  | 'button13Med'
  | 'button12Medium'
  | 'button13MedLined'
  | 'h1'
  | 'h1.5'
  | 'h1.7'
  | 'h2'
  | 'h2.5'
  | 'h3'
  | 'subheader'
  | 't16Regular'
  | 't16Medium'
  | 't18Medium'
  | 't18Regular'
  | 'input15'
  | 'inputSmall'
  | 'inputVerySmall'
  | 'typographyBody2'
  | 'default';

interface TextProps {
  variant?: TextVariant;
  children: ReactNode;
  sx?: SxProps<Theme>;
  component?: ElementType;
  className?: string;
  style?: CSSProperties;
}

const textStyles: Record<TextVariant, SxProps<Theme>> = {
  buttonMedium: { fontSize: '18px', fontWeight: 500, fontFamily: 'Open Sans' },
  buttonSmallRegular: { fontSize: '15px', fontWeight: 400, fontFamily: 'Open Sans' },
  buttonSmallMedium: { fontSize: '15px', fontWeight: 500, fontFamily: 'Open Sans' },
  buttonLinedMed: { fontSize: '15px', fontWeight: 500, fontFamily: 'Open Sans' },
  button14Med: { fontSize: '14px', fontWeight: 500, fontFamily: 'Open Sans' },
  button13Med: { fontSize: '13px', fontWeight: 500, fontFamily: 'Open Sans' },
  button12Medium: { fontSize: '12px', fontWeight: 500, fontFamily: 'Open Sans' },
  button13MedLined: {
    fontSize: '13px',
    fontWeight: 500,
    fontFamily: 'Open Sans',
    textDecoration: 'underline',
  },
  h1: { fontSize: '46px', fontWeight: 700, fontFamily: 'Open Sans' },
  'h1.5': { fontSize: '38px', fontWeight: 700, fontFamily: 'Open Sans' },
  'h1.7': { fontSize: '28px', fontWeight: 600, fontFamily: 'Open Sans' },
  h2: { fontSize: '22px', fontWeight: 600, fontFamily: 'Open Sans' },
  'h2.5': { fontSize: '20px', fontWeight: 600, fontFamily: 'Open Sans' },
  h3: { fontSize: '18px', fontWeight: 600, fontFamily: 'Open Sans' },
  subheader: { fontSize: '16px', fontWeight: 500, fontFamily: 'Open Sans' },
  t16Regular: { fontSize: '16px', fontWeight: 400, fontFamily: 'Open Sans' },
  t16Medium: { fontSize: '16px', fontWeight: 500, fontFamily: 'Open Sans' },
  t18Medium: { fontSize: '18px', fontWeight: 500, fontFamily: 'Open Sans' },
  t18Regular: { fontSize: '18px', fontWeight: 400, fontFamily: 'Open Sans' },
  input15: { fontSize: '15px', fontWeight: 400, fontFamily: 'Open Sans' },
  inputSmall: { fontSize: '14px', fontWeight: 400, fontFamily: 'Open Sans' },
  inputVerySmall: { fontSize: '13px', fontWeight: 400, fontFamily: 'Open Sans' },
  typographyBody2: {
    fontSize: '14px',
    fontWeight: 400,
    fontFamily: 'Open Sans',
    letterSpacing: '-0.5px',
    lineHeight: '143%',
  },
  default: { fontSize: '16px', fontWeight: 400 },
};

export const Text: FC<TextProps> = ({ variant = 'default', children, sx = {}, component, className, style }) => {
  const combinedSx = {
    ...textStyles[variant],
    color: (sx as any)?.color || COLORS.textMain,
    ...(sx as object),
  };
  return (
    <Typography sx={combinedSx} component={component} className={className} style={style}>
      {children}
    </Typography>
  );
};
